import { useCallback, useEffect, useMemo } from "react";

import {
    bsMath,
    getUnixTs,
    TIME,
    SortDirection,
    filterTruthy,
    isBeforeNow,
    isPastNow,
    isAll,
    AllValueType,
    lamportsToUiAmount,
    NullableRecord,
    filterNullableRecord
} from "@bridgesplit/utils";
import { useMemoizedGroupedMap, useMemoizedKeyMap } from "@bridgesplit/ui";
import {
    AbfOrderFundingType,
    FALLBACK_METADATA,
    UncertaintyType,
    WhirlpoolOffchain,
    WhirlpoolPositionExpanded,
    RaydiumPositionExpanded,
    RaydiumOffchainPool,
    PastLoanCollateral,
    LoanPnlStats
} from "@bridgesplit/abf-sdk";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

import {
    marketsPublicApi,
    useLoanDetailQuery,
    useLoanInfosQuery,
    useHistoricalLoansQuery,
    useRaydiumOffchainQuery,
    useWhirlpoolsOffchainQuery,
    useLoanPnlQuery,
    useMergePlanQuoteQuery,
    useUserLoansGroupsQuery,
    useLoanPnlGraphQuery
} from "../reducers";
import type { ApiUserLoanGroup, ApiUserLoopGroup, MergePlanQuoteRequest } from "../types/api/loan";
import {
    BsMetaUtil,
    calculateHealthRatio,
    getLoanTotalDebtForLedger,
    isEventActionMetadataType,
    summarizeLedgerAccount,
    useAbfTypesToUiConverter,
    isCollateralSplMint,
    useGenerateUiExpanded,
    getLoopVaultPairIdentifier,
    calculateNetApyLive
} from "../utils";
import {
    AbfLoanExpanded,
    LoanStatus,
    LoanFilterApi,
    LoanPaginationInfo,
    LoanInfo,
    LedgerExpanded,
    AssetTypeIdentifier,
    Ledger,
    LoanInfoSorting,
    DerivedLoanEventActionType,
    ParsedRepayPrincipalMetadata,
    ParsedLoanEventExpanded,
    PriceFetchType,
    LoanFilterType,
    LoanStatusType,
    Collateral,
    LoansAggregate,
    PositionValueChartData,
    PnlChartData,
    RateHistoryChartData,
    LoanChartsEvent,
    UserLoansFilter,
    TokenListMetadata,
    ApiHistoricalLoanItem,
    ApiHistoricalLoansRequest,
    ApiHistoricalLoanPrincipalEntry,
    ApiHistoricalLoanCollateralEntry,
    ApiClosedLedgerSummary
} from "../types";
import { useOraclePrices } from "./pricing";
import { isWhirlpoolMetadata, useWhirlpoolPositions } from "./whirlpool";
import { LOAN_REFINANCE_GRACE_BEFORE_DEFAULT, MAXIMUM_PAGE_SIZE } from "../constants";
import {
    getClmmPositionIdentifier,
    getClmmPositionMint,
    getClmmPositionType,
    isRaydiumPosition,
    isWhirlpoolPosition
} from "./clmm";
import { LoanCollateralExpanded, type UserLoanGroup, type UserLoopGroup } from "../types/loan";
import { isRaydiumMetadata, useRaydiumPositions } from "./raydium";
import { useTokenListContext } from "./token-list";
import { useLoopscaleWalletContext } from "../wallet";

export type LoanInfosParams = {
    loanFilter: LoanFilterApi;
    // require explicit null to avoid ts error
    pagination?: LoanPaginationInfo | null;
    skip?: boolean;
    /**
     * Whether to bundle the per-loan PnL breakdown into `/loans/info`. Defaults to `true`.
     * PnL is the dominant server cost on this endpoint — pass `false` for surfaces that never
     * render it (e.g. the borrow table, which shows Debt/Collateral/Health/APY but no PnL).
     */
    includePnl?: boolean;
    /**
     * When true, after the current page lands, warm the RTK Query cache for
     * `page + 1` so the next-page click renders instantly instead of
     * spinning. RTK dedupes by arg, so it's safe to leave on across renders.
     */
    prefetchNext?: boolean;
};

export function convertLoanStatusTypeToFilterType(loanStatusType: LoanStatusType): LoanFilterType | undefined {
    switch (loanStatusType) {
        case LoanStatusType.ActiveOnly:
            return LoanFilterType.Active;
        case LoanStatusType.InactiveOnly:
            return LoanFilterType.Closed;
        case LoanStatusType.All:
            return undefined;
    }
}

export type LoanInfoResult = {
    data: AbfLoanExpanded[] | undefined;
    totalLoans: number | undefined;
    aggregate: LoansAggregate | undefined;
    snapshotTs: number | undefined;
    /** Distinct principal mints across the full filter set — for filter dropdowns. */
    principalMints: string[] | undefined;
    /** Distinct collateral mints across the full filter set — for filter dropdowns. */
    collateralMints: string[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

// Server-stamped USD fields (collateralUsd, principalUsd, interestAccruedUsd,
// collateralUsdRisk, principalUsdRisk, collateralBreakdown, pendingYieldUsd) are
// overlaid on each expanded loan so display code can read them without client re-pricing.
export function useLoanInfos({
    loanFilter: loanFilterRaw,
    pagination,
    includePnl = true,
    ...params
}: LoanInfosParams): LoanInfoResult {
    const { getMetadata: getTokenMetadata } = useTokenListContext();

    const loanFilter = useMemo((): LoanFilterApi => {
        const collateralMetadata = pagination?.collateralMint ? getTokenMetadata(pagination.collateralMint) : undefined;
        const isClmm = isClmmMetadata(collateralMetadata);

        return {
            lenders: loanFilterRaw.lenders,
            loanAddresses: loanFilterRaw.loanAddresses ?? [],
            borrowers: loanFilterRaw.borrowers ?? [],
            filterType: convertLoanStatusTypeToFilterType(pagination?.loanStatusType ?? LoanStatusType.All),
            principalMints: pagination?.principalMint ? [pagination.principalMint] : [],
            pageSize: pagination?.pageSize,
            page: pagination?.page,
            sortSide: pagination?.sortSide ?? SortDirection.Asc,
            sortType: pagination?.sortType ?? LoanInfoSorting.StartTime,
            collateralMints:
                pagination?.collateralMint && !isClmm
                    ? [pagination.collateralMint]
                    : (loanFilterRaw.collateralMints ?? []),
            collateralIdentifiers:
                pagination?.collateralMint && isClmm
                    ? [pagination.collateralMint]
                    : (loanFilterRaw.collateralIdentifiers ?? []),
            orderFundingTypes: loanFilterRaw.orderFundingTypes,
            loopVaultPrincipalCollateralPairs: loanFilterRaw.loopVaultPrincipalCollateralPairs,
            // Bundle the per-loan pnl breakdown into /loans/info so we don't need a
            // follow-up /loans/pnl roundtrip. PnL is the dominant server cost here, so callers
            // that never render it (the borrow table) opt out via `includePnl: false`.
            includePnl
        };
    }, [loanFilterRaw, pagination, getTokenMetadata, includePnl]);

    const skip = params?.skip;
    const listQuery = useLoanInfosQuery(loanFilter, { skip });
    const rawData = listQuery.data;

    // Next-page prefetch (opt-in via `prefetchNext: true`). Once the current
    // page lands and we know `pageInfo.totalItems`, warm the RTK cache for
    // `page + 1` so the next-page click reads from cache. RTK dedupes by arg
    // and won't refetch if the entry is fresh.
    const prefetchLoanInfos = marketsPublicApi.usePrefetch("loanInfos");
    const nextPageFilter = useMemo((): LoanFilterApi | undefined => {
        if (!params?.prefetchNext || skip) return undefined;
        const currentPage = loanFilter.page ?? 0;
        const currentPageSize = loanFilter.pageSize ?? 0;
        if (currentPageSize <= 0) return undefined;
        const totalItems = rawData?.pageInfo?.totalItems;
        // Skip when we already know there is no next page.
        if (totalItems !== undefined && (currentPage + 1) * currentPageSize >= totalItems) return undefined;
        return { ...loanFilter, page: currentPage + 1 };
    }, [params?.prefetchNext, skip, loanFilter, rawData?.pageInfo?.totalItems]);
    useEffect(() => {
        if (nextPageFilter && !listQuery.isFetching) {
            prefetchLoanInfos(nextPageFilter);
        }
    }, [nextPageFilter, listQuery.isFetching, prefetchLoanInfos]);

    const { data, isLoading, isFetching } = useExpandLoans(rawData?.items, {
        skip,
        sourceLoading: listQuery.isLoading,
        sourceFetching: listQuery.isFetching,
        includePnl
    });

    return {
        data,
        totalLoans: rawData?.pageInfo.totalItems,
        aggregate: rawData?.aggregate,
        snapshotTs: rawData?.snapshotTs,
        principalMints: rawData?.principalMints,
        collateralMints: rawData?.collateralMints,
        isLoading,
        isFetching
    };
}

export type HistoricalLoansParams = {
    borrower?: string;
    lenders?: string[];
    loanAddresses?: string[];
    orderFundingTypes?: AbfOrderFundingType;
    pagination?: LoanPaginationInfo | null;
    skip?: boolean;
};

export type HistoricalLoansResult = {
    data: ApiHistoricalLoanItem[] | undefined;
    totalLoans: number | undefined;
    principalMints: string[] | undefined;
    collateralMints: string[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

export function useHistoricalLoans({
    borrower,
    lenders,
    loanAddresses,
    orderFundingTypes,
    pagination,
    skip
}: HistoricalLoansParams): HistoricalLoansResult {
    const { getMetadata } = useTokenListContext();
    const request = useMemo((): ApiHistoricalLoansRequest => {
        return {
            borrower,
            lenders,
            loanAddresses,
            principalMints: pagination?.principalMint ? [pagination.principalMint] : undefined,
            collateralMints: pagination?.collateralMint ? [pagination.collateralMint] : undefined,
            orderFundingTypes,
            page: pagination?.page,
            pageSize: pagination?.pageSize,
            sortSide: pagination?.sortSide
        };
    }, [borrower, lenders, loanAddresses, orderFundingTypes, pagination]);

    // BE rejects requests with no scoping filter (400). Skip the call entirely until one is set.
    const hasScope = !!borrower || (lenders?.length ?? 0) > 0 || (loanAddresses?.length ?? 0) > 0;
    const query = useHistoricalLoansQuery(request, { skip: skip || !hasScope });

    // Items arrive with raw-lamport avgBalance values per the BE contract; convert to UI
    // amounts here so consumers can render with BsMetaUtil.formatAmount the same way they
    // render active-loan ledger/collateral fields (which go through convertLoanInfo).
    const data = useMemo(() => {
        if (!query.data?.items) return undefined;
        return query.data.items.map((item) => ({
            ...item,
            principal: item.principal.map((p) => ({
                ...p,
                avgBalance: lamportsToUiAmount(p.avgBalance, getMetadata(p.principal)?.decimals)
            })),
            collateral: item.collateral.map((c) => ({
                ...c,
                avgBalance: lamportsToUiAmount(c.avgBalance, getMetadata(c.collateral)?.decimals)
            }))
        }));
    }, [query.data, getMetadata]);

    return {
        data,
        totalLoans: query.data?.pageInfo.totalItems,
        principalMints: query.data?.principalMints,
        collateralMints: query.data?.collateralMints,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    };
}

export function useLoanDetail(
    loanAddress: string | undefined,
    { includePnl = true }: { includePnl?: boolean } = {}
): {
    data: AbfLoanExpanded | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const detailQuery = useLoanDetailQuery(loanAddress ? { address: loanAddress, includePnl } : skipToken, {
        skip: !loanAddress
    });
    const loans = useMemo(() => (detailQuery.data ? [detailQuery.data] : undefined), [detailQuery.data]);
    const { data, isLoading, isFetching } = useExpandLoans(loans, {
        skip: !loanAddress,
        sourceLoading: detailQuery.isLoading,
        sourceFetching: detailQuery.isFetching,
        includePnl
    });
    return {
        data: data?.[0],
        isLoading,
        isFetching
    };
}

function useExpandLoans(
    loans: LoanInfo[] | undefined,
    {
        skip,
        sourceLoading,
        sourceFetching,
        includePnl = true
    }: { skip?: boolean; sourceLoading: boolean; sourceFetching: boolean; includePnl?: boolean }
): { data: AbfLoanExpanded[] | undefined; isLoading: boolean; isFetching: boolean } {
    // Addresses whose pnl breakdown wasn't bundled on the source response. Both
    // /loans/info (with includePnl:true) and /loans/:address?includePnl=true return it inline,
    // so this fallback only fires for callers that opt out of the bundled pnl.
    const loansMissingPnl = useMemo(
        () =>
            loans
                ?.filter((l) => !l.pnl)
                .map((l) => l.loan.address)
                .filter(filterTruthy) ?? [],
        [loans]
    );

    const { mints, whirlpoolMints, whirlpoolAddresses, raydiumMints, raydiumAddresses } =
        useLoanInfoResponseMints(loans);

    const { data: whirlpools, isFetching: whirlpoolsFetching } = useWhirlpoolPositions(whirlpoolMints);
    const { data: raydiumPools, isFetching: raydiumPoolsFetching } = useRaydiumPositions(raydiumMints);

    const nftGetKey = useCallback((r: RaydiumPositionExpanded) => r.position.nftMint, []);
    const whirlpoolGetKey = useCallback((w: WhirlpoolPositionExpanded) => w.position.positionMint, []);

    const positionNftToPool = useMemoizedKeyMap(raydiumPools, nftGetKey);
    const positionToWhirlpool = useMemoizedKeyMap(whirlpools, whirlpoolGetKey);

    const { data: whirlpoolsOffchain } = useWhirlpoolsOffchainQuery(
        whirlpoolAddresses?.length ? { addresses: whirlpoolAddresses } : skipToken
    );

    const { data: raydiumOffchain } = useRaydiumOffchainQuery(
        raydiumAddresses?.length ? { ids: raydiumAddresses } : skipToken
    );

    const whirlpoolOffchainGetKey = useCallback((w: WhirlpoolOffchain) => w.address, []);
    const raydiumOffchainGetKey = useCallback((r: RaydiumOffchainPool) => r.id, []);

    const whirlpoolToOffchain = useMemoizedKeyMap(whirlpoolsOffchain?.data ?? [], whirlpoolOffchainGetKey);
    const raydiumToOffchain = useMemoizedKeyMap(raydiumOffchain?.data ?? [], raydiumOffchainGetKey);
    const { getUsdPrice, getOracle, isLoading: pricesLoading } = useOraclePrices(mints, PriceFetchType.Twap);

    const getClmmPosition = useCallback(
        (assetMint: string) => {
            const whirlpool = positionToWhirlpool?.get(assetMint);
            if (isWhirlpoolPosition(whirlpool)) return whirlpool;
            const raydium = positionNftToPool?.get(assetMint);
            if (isRaydiumPosition(raydium)) return raydium;
            return undefined;
        },
        [positionToWhirlpool, positionNftToPool]
    );

    const getUsdPriceWithClmmPosition = useCallback(
        (assetMint: string, assetType?: AssetTypeIdentifier) => {
            let usdPrice = getUsdPrice(assetMint, UncertaintyType.Skip);
            if (assetType === AssetTypeIdentifier.OrcaPosition) {
                const whirlpool = positionToWhirlpool?.get(assetMint);
                if (whirlpool && whirlpool.totalPrice !== undefined) {
                    usdPrice = whirlpool.totalPrice;
                }
            } else if (assetType === AssetTypeIdentifier.RaydiumPosition) {
                const raydium = positionNftToPool?.get(assetMint);
                if (raydium && raydium.totalPrice !== undefined) {
                    usdPrice = raydium.totalPrice;
                }
            }
            return usdPrice;
        },
        [getUsdPrice, positionToWhirlpool, positionNftToPool]
    );

    const { createPastCollateralExpanded } = useGenerateUiExpanded();
    const {
        tokensLoading,
        getMetadata,
        convertParsedLoanEventsToDerivedEvents: convertParsedLoanEventsToUiEvents,
        convertLoanInfo,
        convertLoanPnlStats
    } = useAbfTypesToUiConverter();

    const {
        data: loanPnl,
        isLoading: isLoanPnlLoading,
        isFetching: isLoanPnlFetching
    } = useLoanPnlQuery(
        {
            loans: loansMissingPnl,
            includePnl: true
        },
        // When the caller opts out of PnL (`includePnl: false`), don't backfill it from the
        // dedicated /loans/pnl endpoint either — otherwise the cost just relocates to a serial
        // follow-up call. Surfaces that never render PnL (the borrow table) skip it entirely.
        { skip: !loansMissingPnl.length || skip || !includePnl }
    );

    const isFetching = sourceFetching || whirlpoolsFetching || raydiumPoolsFetching || isLoanPnlFetching;
    const isLoading = sourceLoading || tokensLoading || pricesLoading || isFetching || isLoanPnlLoading;

    const data: AbfLoanExpanded[] | undefined = useMemo(() => {
        if (isLoading || isFetching) return undefined;
        if (skip || !loans) return [];

        const validLoans = loans
            .map((rawLoanInfoResponse: LoanInfo): NullableRecord<AbfLoanExpanded> | undefined => {
                const loanClosed = rawLoanInfoResponse.loan.closed;
                const defaultLedger = getRawZcLedger(rawLoanInfoResponse);

                // Liquidated loans keep collateral but no ledgers; the BE derives their mint from events.
                const principalMint = defaultLedger?.principalMint ?? rawLoanInfoResponse.principalMint ?? undefined;
                const principalMetadata = principalMint ? getMetadata(principalMint) : FALLBACK_METADATA;

                if (!principalMetadata) {
                    return undefined;
                }

                const loanInfoResponse: LoanInfo = convertLoanInfo(rawLoanInfoResponse);

                const ledgerExpanded: LedgerExpanded[] = loanInfoResponse.ledgers.map((l) => ({
                    ...l,
                    ledgerDebt: getLoanTotalDebtForLedger(l),
                    isFilled: l.principalDue === l.principalRepaid
                }));

                const pastLedgerExpanded: LedgerExpanded[] = loanInfoResponse.pastLedgers.map((l) => ({
                    ...l,
                    ledgerDebt: getLoanTotalDebtForLedger(l),
                    isFilled: l.principalDue === l.principalRepaid
                }));

                const loanCollateralExpanded: LoanCollateralExpanded[] = loanInfoResponse.collateral.map((c) => {
                    const metadata = getMetadata(c.assetIdentifier);
                    const whirlpoolOffchain = whirlpoolToOffchain?.get(c.assetIdentifier);
                    const raydiumOffchain = raydiumToOffchain?.get(c.assetIdentifier);

                    return {
                        ...c,
                        metadata: metadata ? { ...metadata, whirlpoolOffchain, raydiumOffchain } : FALLBACK_METADATA,
                        usdPrice: getUsdPriceWithClmmPosition(c.assetMint, metadata?.assetType),
                        uncertainty: getOracle(c.assetIdentifier)?.uncertainty,
                        clmmPosition: getClmmPosition(c.assetMint),
                        oracleAccount: getOracle(c.assetIdentifier)?.oracleAccount
                    };
                });

                const matrixUpdates = rawLoanInfoResponse.matrixUpdates ?? [];
                const parsedLoanEvents: ParsedLoanEventExpanded[] = convertParsedLoanEventsToUiEvents(
                    loanInfoResponse.events,
                    matrixUpdates
                );

                const loanInfo: LoanInfo = {
                    loan: rawLoanInfoResponse.loan,
                    loanType: rawLoanInfoResponse.loanType,
                    ledgers: rawLoanInfoResponse.ledgers,
                    pastLedgers: pastLedgerExpanded,
                    collateral: loanCollateralExpanded,
                    matrixUpdates,
                    events: loanInfoResponse.events,
                    collateralYieldPct: rawLoanInfoResponse.collateralYieldPct ?? 0,
                    principalYieldPct: rawLoanInfoResponse.principalYieldPct ?? 0,
                    collateralUsd: rawLoanInfoResponse.collateralUsd,
                    principalUsd: rawLoanInfoResponse.principalUsd,
                    interestAccruedUsd: rawLoanInfoResponse.interestAccruedUsd,
                    collateralUsdRisk: rawLoanInfoResponse.collateralUsdRisk,
                    principalUsdRisk: rawLoanInfoResponse.principalUsdRisk,
                    collateralBreakdown: rawLoanInfoResponse.collateralBreakdown,
                    pendingYieldUsd: rawLoanInfoResponse.pendingYieldUsd,
                    pnlUsd: rawLoanInfoResponse.pnlUsd,
                    // Only attach closedSummary when set. filterNullableRecord rejects records
                    // with any explicit `undefined` field, so an undefined closedSummary on
                    // active loans would drop the whole row.
                    ...(loanInfoResponse.closedSummary !== undefined && {
                        closedSummary: loanInfoResponse.closedSummary
                    })
                };

                const pastLoanCollateralItems = createPastCollateralExpanded(
                    parsedLoanEvents,
                    positionToWhirlpool,
                    getUsdPrice
                );

                const lenders = [...new Set(rawLoanInfoResponse.ledgers.map((entry) => entry.strategy))];
                const loanInfoExpanded: AbfLoanExpanded = {
                    ...loanInfo,
                    index: 0,
                    status: LoanStatus.OngoingLoan,
                    borrower: rawLoanInfoResponse.loan.borrower,
                    lenders,
                    borrowerWAvgApy: calculateBorrowerWeightedAvgApy(loanClosed ? pastLedgerExpanded : ledgerExpanded),
                    principalMetadata,
                    // Closed-loan list rows arrive with `ledgers: []` and no past-ledger snapshot,
                    // so principalMint is undefined and the oracle lookup returns undefined.
                    // Default to 0 so the row survives filterNullableRecord and renders with the
                    // collateral-only data we do have.
                    principalUsdPrice: getUsdPrice(principalMint, UncertaintyType.Skip) ?? 0,
                    principalMarketPrice: getOracle(principalMint)?.marketPrice,
                    pastCollateral: pastLoanCollateralItems,
                    ledger: ledgerExpanded,
                    events: parsedLoanEvents,
                    pnlStats: null,
                    pastLedgers: pastLedgerExpanded,
                    collateral: loanCollateralExpanded
                };

                const pnlStats = convertLoanPnlStats(
                    rawLoanInfoResponse.pnl ?? loanPnl?.[loanInfoExpanded.loan.address],
                    loanInfoExpanded?.principalMetadata?.decimals,
                    getMetadata(
                        loanCollateralExpanded[0]?.metadata?.mint ?? loanInfoExpanded?.pastCollateral[0]?.metadata?.mint
                    )?.decimals
                );

                return {
                    ...loanInfoExpanded,
                    pnlStats
                };
            })
            .filter(filterTruthy)
            .filter(filterNullableRecord)
            .map((loanExpanded, index) => {
                const ledger = getZcLedger(loanExpanded);
                const collateral = getZcLoanCollateral(loanExpanded);
                const isActive = isLoanActive(loanExpanded);

                const netApyLive = isActive
                    ? calculateNetApyLive({
                          loanCollateral: collateral,
                          collateralUsdValue: loanExpanded.collateralUsd,
                          collateralApyPct: loanExpanded.collateralYieldPct,
                          totalDebtUsdValue: loanExpanded.principalUsd,
                          principalApyPct: ledger?.apy ?? 0,
                          principalYieldPct: loanExpanded.principalYieldPct
                      })
                    : 0;

                const loanLoopVaultPairIdentifier = getLoopVaultPairIdentifier(loanExpanded);
                return {
                    ...loanExpanded,
                    index,
                    status: getLoanStatusFromLoan(loanExpanded),
                    loanLoopVaultPairIdentifier,
                    netApyLive: netApyLive ?? 0
                };
            });

        return validLoans;
    }, [
        isLoading,
        isFetching,
        skip,
        loans,
        getMetadata,
        convertLoanInfo,
        convertParsedLoanEventsToUiEvents,
        createPastCollateralExpanded,
        positionToWhirlpool,
        getUsdPrice,
        convertLoanPnlStats,
        loanPnl,
        whirlpoolToOffchain,
        raydiumToOffchain,
        getUsdPriceWithClmmPosition,
        getOracle,
        getClmmPosition
    ]);

    return {
        data: isLoading ? undefined : data,
        isLoading,
        isFetching
    };
}

export function getZcLoanEndTime(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) {
        return undefined;
    }
    const firstLedger = loanInfo.ledger[0];
    return firstLedger?.endTime;
}
export function getLoanRepaidAmount(loanExpanded: AbfLoanExpanded | undefined) {
    if (loanExpanded === undefined) {
        return 0;
    }

    return loanExpanded.events.reduce((prev, curr) => {
        if (isEventActionMetadataType(curr.actionMetadata, DerivedLoanEventActionType.RepayPrincipal)) {
            return curr.amount + prev;
        } else {
            return prev;
        }
    }, 0);
}

export function isLoanActive(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    return [LoanStatus.OngoingLoan].includes(loanExpanded.status) || isLoanInRefinanceGrace(loanExpanded);
}
export function isLoanInRefinanceGrace(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    return [LoanStatus.RefinanceGrace].includes(loanExpanded.status);
}

// Active loan whose maturity has passed (includes refinance grace)
export function isLoanPastDue(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded || !isLoanActive(loanExpanded)) return false;
    const endTime = getZcLoanEndTime(loanExpanded);
    return endTime !== undefined && isPastNow(endTime);
}

export function calculateAmountRepaid(metaData: ParsedRepayPrincipalMetadata) {
    const principalDelta =
        metaData.postRepaymentPrincipalLedgerState.principalRepaid -
        metaData.preRepaymentPrincipalLedgerState.principalRepaid;

    const interestDelta =
        metaData.preRepaymentPrincipalLedgerState.interestOutstanding -
        metaData.postRepaymentPrincipalLedgerState.interestOutstanding;

    return principalDelta + interestDelta;
}

export function isLoanComplete(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;
    return loanInfo.loan.closed;
}

export function isLoanWaitingLiquidation(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;

    if (!isBeforeNow((getZcLoanEndTime(loanInfo) ?? 0) + LOAN_REFINANCE_GRACE_BEFORE_DEFAULT)) {
        const ledgersPastDue = loanInfo?.ledger.filter((data) => !isLedgerInRefinanceGrace(loanInfo, data));
        return ledgersPastDue?.length > 0;
    }

    return false;
}

export function isLoanDefaulted(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;
    return loanInfo.status === LoanStatus.DefaultedLoan;
}

export function isLoanLiquidated(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;

    return loanInfo.events.some((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.FullLiquidation)
    );
}

// can be called on a fully liquidated loan, on a loan thats in temporary default standing
export function getLoanDefaultedDate(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo || !isLoanDefaulted(loanInfo)) return undefined;

    if (loanInfo.loan.closed) return loanInfo.pastLedgers.reduce((prev, curr) => Math.max(prev, curr.endTime), 0);

    return loanInfo.ledger.reduce((prev, curr) => Math.max(prev, curr.endTime), 0);
}

function getFullLiquidationEvent(events: ParsedLoanEventExpanded[]): ParsedLoanEventExpanded | undefined {
    const fullLiquidationEvent = events.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.FullLiquidation)
    );
    return fullLiquidationEvent;
}

function getMaxRepaidEventTimeFromEvents(loanInfo: AbfLoanExpanded) {
    const events = loanInfo.events;

    const fullLiquidationEvent = getFullLiquidationEvent(events);
    if (fullLiquidationEvent?.eventTime) {
        return fullLiquidationEvent.eventTime;
    }

    const repaidEvents = events.filter((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RepayPrincipal)
    );
    if (!repaidEvents.length) return loanInfo.loan.lastInteractedTime;
    return Math.max(...repaidEvents.map((e) => e.eventTime));
}

export function getLoanCompletedDate(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo || !isLoanComplete(loanInfo)) return undefined;
    return getMaxRepaidEventTimeFromEvents(loanInfo);
}

export function isLedgerInRefinanceGrace(loan: AbfLoanExpanded | undefined, ledger: LedgerExpanded | undefined) {
    if (!loan || !ledger || (ledger && ledger.isFilled)) return false;
    const isRefinanceGrace = ledger.endTime + LOAN_REFINANCE_GRACE_BEFORE_DEFAULT > getUnixTs();
    const isRefinancePeriod = ledger.endTime < getUnixTs();

    if (isRefinanceGrace && isRefinancePeriod) return true;

    return false;
}

export enum LoanHealthStatus {
    Healthy = "Healthy",
    Warning = "Warning",
    Danger = "Danger"
}

export interface CollateralLoanHealthChange {
    newHealth: number;
    originalHealth: number;
    originalCollateralValueUsd: number;
    newLoanCollateralValueUsd: number;
    liquidationThreshold: number | undefined;
    strategyLtv: number;
    originalHealthStatus: LoanHealthStatus;
    newHealthStatus: LoanHealthStatus;
}

export interface LoanHealthDetails {
    health: number;
    ltv: number;
    loanCollateralValue: number;
    totalOwedValue: number;
    liquidationThreshold: number | undefined;
    loanHealthStatus: LoanHealthStatus;
}

export function getHealthStatus(
    loanLtv: number | undefined,
    strategyLtv: number | undefined,
    strategyLqt: number | undefined
): LoanHealthStatus {
    if (loanLtv === undefined || strategyLtv === undefined || strategyLqt === undefined) {
        return LoanHealthStatus.Healthy;
    }

    const ltvThreshold = strategyLtv + 0.01; // LTV + 1%
    const liquidationThreshold = strategyLqt - 0.02; // LQT - 2%

    if (loanLtv > liquidationThreshold) {
        return LoanHealthStatus.Danger; // Red - beyond LQT-2%
    } else if (loanLtv >= ltvThreshold) {
        return LoanHealthStatus.Warning; // Yellow - between LTV+1% and LQT-2%
    } else {
        return LoanHealthStatus.Healthy; // Green - below LTV+1%
    }
}

// Uses first ledgerIndexToLtvInfo since only single asset collateral supported.
// Reads server-stamped risk-adjusted USD (collateralUsdRisk, principalUsdRisk,
// interestAccruedUsd) from the expanded loan.
export function calculateLoanHealth(loanExpanded: AbfLoanExpanded | undefined): LoanHealthDetails {
    if (!loanExpanded)
        return {
            health: 0,
            ltv: 0,
            loanCollateralValue: 0,
            totalOwedValue: 0,
            liquidationThreshold: 0,
            loanHealthStatus: LoanHealthStatus.Healthy
        };

    const loanCollateralValue = getLoanCollateralUsdValue(loanExpanded);
    const totalOwedValue = getLoanDebtUsdAtRisk(loanExpanded);

    if (totalOwedValue === 0) {
        return {
            health: Number.POSITIVE_INFINITY,
            ltv: 0,
            loanCollateralValue,
            totalOwedValue,
            liquidationThreshold: 0,
            loanHealthStatus: LoanHealthStatus.Healthy
        };
    }

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);
    const strategyLtv = getZcLoanLtv(loanExpanded);

    const ltv = loanCollateralValue > 0 ? totalOwedValue / loanCollateralValue : 0;

    const health = calculateHealthRatio(totalOwedValue, loanCollateralValue, liquidationThreshold);

    return {
        health,
        ltv,
        loanCollateralValue,
        totalOwedValue,
        liquidationThreshold,
        loanHealthStatus: getHealthStatus(ltv, strategyLtv, liquidationThreshold)
    };
}

export function getLoanCollateralUsdValue(loanExpanded: AbfLoanExpanded | undefined): number {
    return loanExpanded?.collateralUsdRisk ?? 0;
}

export function getLoanCollateralMarketUsdValue(loanExpanded: AbfLoanExpanded | undefined): number {
    return (
        loanExpanded?.collateralBreakdown?.reduce((total, collateral) => total + collateral.usd, 0) ??
        loanExpanded?.collateralUsd ??
        0
    );
}

// Debt-at-risk: principal + accrued interest, valued at the risk-adjusted (upper-bound)
// principal price. `principalUsdRisk` is only `principal × riskPrice`, so we scale interest
// by the implied risk ratio to avoid under-counting the debt leg.
function getLoanDebtUsdAtRisk(loanExpanded: AbfLoanExpanded): number {
    const riskRatio = loanExpanded.principalUsd > 0 ? loanExpanded.principalUsdRisk / loanExpanded.principalUsd : 1;
    return loanExpanded.principalUsdRisk + loanExpanded.interestAccruedUsd * riskRatio;
}

export function canLenderReclaimCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;

    if (!isLoanLiquidated(loanExpanded) && !isLoanDefaulted(loanExpanded)) return false;
    return true;
}

export function isLoanFullyLiquidated(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;

    const fullLiquidationEvent = getFullLiquidationEvent(loanExpanded.events);
    return !!fullLiquidationEvent;
}

export function getLoanStatusFromLoan(loanExpanded: AbfLoanExpanded): LoanStatus {
    const firstLedger = getZcLedger(loanExpanded);
    if (isLedgerInRefinanceGrace(loanExpanded, firstLedger) || isLoanWaitingLiquidation(loanExpanded))
        return LoanStatus.RefinanceGrace;
    if (isLoanFullyLiquidated(loanExpanded)) return LoanStatus.DefaultedLoan;
    if (isLoanComplete(loanExpanded)) return LoanStatus.RepaidLoan;

    return LoanStatus.OngoingLoan;
} // Make sure this import exists or adjust as needed

export function getLoanOrcaCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    const collateral = loanExpanded?.collateral.find((c) => !!c.clmmPosition && !!c.amount);
    if (collateral && collateral.clmmPosition && isWhirlpoolPosition(collateral.clmmPosition)) {
        return collateral.clmmPosition;
    }
    return undefined;
}

export function getLoanRaydiumCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    const collateral = loanExpanded?.collateral.find((c) => !!c.clmmPosition && !!c.amount);
    if (collateral && collateral.clmmPosition && isRaydiumPosition(collateral.clmmPosition)) {
        return collateral.clmmPosition;
    }
    return undefined;
}

export function getLoanCollateralIdentifier(collateral: LoanCollateralExpanded | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionIdentifier(collateral.clmmPosition);
    }
    return collateral.assetIdentifier;
}
export function getLoanCollateralMint(collateral: LoanCollateralExpanded | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionMint(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function getCollateralIdentifier(collateral: Collateral | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionIdentifier(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function getCollateralMint(collateral: Collateral | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionMint(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function isClmmCollateral(collateral: Collateral | undefined): boolean | undefined {
    if (!collateral) {
        return undefined;
    }
    const clmmPositionType = getClmmPositionType(collateral.clmmPosition ?? undefined);
    return (
        clmmPositionType === AssetTypeIdentifier.OrcaPosition ||
        clmmPositionType === AssetTypeIdentifier.RaydiumPosition
    );
}

export function isClmmMetadata(metadata: TokenListMetadata | undefined): boolean {
    if (!metadata) return false;
    return isWhirlpoolMetadata(metadata) || isRaydiumMetadata(metadata);
}

export function getLoanStrategyIdentifier(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return undefined;
    return loanExpanded.loan.nonce;
}
export function getZcLedgerStrategyIdentifier(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;
    return firstLedger.strategy;
}

export function getZcLoanCollateral(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded | undefined {
    if (!loanExpanded?.collateral || loanExpanded.collateral.length === 0) {
        return undefined;
    }

    return loanExpanded.collateral[0];
}

/** Row key aligned with `UserLoopGroup` / `createRows`: `collateralMint|collateralIdentifier`. */
export function loanCollateralGroupTableKey(loan: AbfLoanExpanded): string {
    const c = getZcLoanCollateral(loan);
    if (!c) return "";
    return `${c.assetMint}|${c.assetIdentifier}`;
}

/** Table key for a {@link UserLoopGroup} summary row; aligned with {@link loanCollateralGroupTableKey}. */
export function userLoopGroupTableKey(g: { collateralMint: string; collateralIdentifier: string }): string {
    return `${g.collateralMint}|${g.collateralIdentifier}`;
}

/** Row key aligned with {@link UserLoanGroup} / `createRows`: principal mint. */
export function loanPrincipalGroupTableKey(loan: AbfLoanExpanded): string {
    return loan.principalMetadata?.mint ?? "";
}

/** Table key for a {@link UserLoanGroup} (non-loop) summary row. */
export function userLoanGroupTableKey(g: { principalMint: string }): string {
    return g.principalMint;
}

/**
 * Distinct `loan_collateral.asset_identifier` values for `POST /loans/info` when scoping to
 * visible portfolio groups. Matches the suffix of {@link loanCollateralGroupTableKey}.
 * Use **identifiers only** (leave `collateralMints` empty on the request): the markets SQL
 * treats `asset_identifier IN (...)` as OR across those groups and avoids AND-ing mints
 * with identifiers, which would over-restrict.
 */
export function uniqueCollateralIdentifiersFromLoanGroupKeys(keys: readonly string[]): string[] {
    const out = new Set<string>();
    for (const key of keys) {
        const i = key.indexOf("|");
        if (i === -1 || i + 1 >= key.length) continue;
        const ident = key.slice(i + 1);
        if (ident.length > 0) {
            out.add(ident);
        }
    }
    return [...out];
}

// Ledger ratios are per collateral slot, and the live collateral isn't always in slot 0
// (a split rollover leaves the new PT in slot 1).
export function getLoanCollateralSlotRatio(
    ratios: number[] | undefined,
    loanExpanded: AbfLoanExpanded | undefined
): number | undefined {
    if (!ratios) return undefined;
    const slot = getZcLoanCollateral(loanExpanded)?.index;
    return slot !== undefined ? ratios[slot] : Math.max(...ratios);
}

export function getZcLoanLtv(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return getLoanCollateralSlotRatio(firstLedger.ltvRatios, loanExpanded);
}
export function getZcLoanPastLtv(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcPastLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return getLoanCollateralSlotRatio(firstLedger.ltvRatios, undefined);
}
export function getZcLoanLiquidationThreshold(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return getLoanCollateralSlotRatio(firstLedger.lqtRatios, loanExpanded);
}

export function getZcLoanPastLiquidationThreshold(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcPastLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return getLoanCollateralSlotRatio(firstLedger.lqtRatios, undefined);
}

export function getZcLedger(loanExpanded: AbfLoanExpanded | undefined): LedgerExpanded | undefined {
    if (!loanExpanded) return undefined;

    // AbfLoanExpanded case
    if (!loanExpanded.ledger || loanExpanded.ledger.length === 0) {
        return undefined;
    }
    return loanExpanded.ledger.find((l) => l.ledgerIndex === 0);
}

export function getZcPastLedger(loanExpanded: AbfLoanExpanded | undefined): LedgerExpanded | undefined {
    if (!loanExpanded) return undefined;
    if (!loanExpanded.pastLedgers || loanExpanded.pastLedgers.length === 0) {
        return undefined;
    }
    return loanExpanded.pastLedgers.find((l) => l.ledgerIndex === 0);
}

export function getPastOrPresentZcLedger(loanExpanded: AbfLoanExpanded | undefined) {
    return isLoanComplete(loanExpanded) ? getZcPastLedger(loanExpanded) : getZcLedger(loanExpanded);
}

export function getPastCollateral(loanExpanded: AbfLoanExpanded | undefined): PastLoanCollateral | undefined {
    if (!loanExpanded) return undefined;
    return isLoanComplete(loanExpanded) ? loanExpanded.pastCollateral[0] : undefined;
}

export function getPastOrPresentZcLoanCollateralMint(loanExpanded: AbfLoanExpanded | undefined): string | undefined {
    if (!loanExpanded) return undefined;
    const collateralMint = isLoanComplete(loanExpanded)
        ? getPastCollateral(loanExpanded)?.metadata.mint
        : getZcLoanCollateral(loanExpanded)?.assetMint;
    return collateralMint;
}

// This function is used to process the loan info response. If there is no ledger info, we default to past ledgers.
export function getRawZcLedger(loanExpanded: LoanInfo | undefined): Ledger | undefined {
    if (!loanExpanded) return undefined;

    return (
        loanExpanded.ledgers.find((l) => l.ledgerIndex === 0) ||
        loanExpanded.pastLedgers.find((l) => l.ledgerIndex === 0)
    );
}

export function getZcCollateral(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded | undefined {
    if (!loanExpanded?.collateral || loanExpanded.collateral.length === 0) {
        return undefined;
    }

    return loanExpanded.collateral[0];
}

// Closed-loan snapshots can carry multiple principal/collateral entries (refinances
// mid-life). For now we surface the last entry — i.e. the principal/collateral at
// the time the loan closed — and return undefined when the array is empty so callers
// don't need to bounds-check.
export function getLastHistoricalPrincipal(
    item: ApiHistoricalLoanItem | ApiClosedLedgerSummary | undefined
): ApiHistoricalLoanPrincipalEntry | undefined {
    if (!item || item.principal.length === 0) return undefined;
    return item.principal[item.principal.length - 1];
}

export function getLastHistoricalCollateral(
    item: ApiHistoricalLoanItem | ApiClosedLedgerSummary | undefined
): ApiHistoricalLoanCollateralEntry | undefined {
    if (!item || item.collateral.length === 0) return undefined;
    return item.collateral[item.collateral.length - 1];
}

export function isLoanBorrower(loanExpanded: AbfLoanExpanded | undefined, user: string | undefined) {
    if (!loanExpanded || !user) return false;
    return loanExpanded.borrower === user;
}

// Only supports single asset collateral
export function getLoanSymbols(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return { principalSymbol: "", collateralSymbol: "", shortCollateralSymbol: undefined };
    const principalSymbol = BsMetaUtil.getSymbol(loanExpanded?.principalMetadata);

    if (isLoanComplete(loanExpanded)) {
        if (!loanExpanded.pastCollateral.length) return { principalSymbol, collateralSymbol: "" };
        const collateralSymbol = BsMetaUtil.getSymbol(loanExpanded.pastCollateral[0].metadata);
        const shortCollateralSymbol = BsMetaUtil.getSymbol(loanExpanded.pastCollateral[0].metadata, {
            shortSymbol: true
        });
        return { principalSymbol, collateralSymbol, shortCollateralSymbol };
    }
    const collateral = getZcCollateral(loanExpanded);
    if (!collateral) return { principalSymbol, collateralSymbol: "", shortCollateralSymbol: undefined };
    if (
        isWhirlpoolMetadata(collateral.metadata) && // todo: when raydium live, add a util fn that encompasses all assetType cases
        collateral.metadata.whirlpoolOffchain
    ) {
        return {
            principalSymbol,
            collateralSymbol: getWhirlpoolName(collateral.metadata.whirlpoolOffchain),
            shortCollateralSymbol: undefined
        };
    }
    const collateralSymbol = BsMetaUtil.getSymbol(collateral.metadata);
    const shortCollateralSymbol = BsMetaUtil.getSymbol(collateral.metadata, { shortSymbol: true });
    return { principalSymbol, collateralSymbol, shortCollateralSymbol };
}

function getWhirlpoolName(whirlpool: WhirlpoolOffchain): string {
    return whirlpool.tokenA.symbol + "-" + whirlpool.tokenB.symbol;
}

export function getUnclaimedAssets(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded[] | undefined {
    // Only supports unclaimed assets for closed loans
    if (!loanExpanded?.loan.closed) {
        return loanExpanded?.collateral.filter((c) => c.amount);
    }
    return undefined;
}

// Will need to use principal prices for multi-principal loans
function calculateBorrowerWeightedAvgApy(ledgers: Ledger[]): number {
    if (!ledgers.length) return 0;

    const totalPrincipalDue = ledgers.reduce((sum, ledger) => sum + ledger.principalDue, 0);
    if (totalPrincipalDue === 0) return 0;

    return ledgers.reduce((sum, ledger) => sum + (ledger.apy * ledger.principalDue) / totalPrincipalDue, 0);
}

export function isManageLeverageEligable(loanExpanded: AbfLoanExpanded | undefined): boolean {
    const collateral = getZcCollateral(loanExpanded);
    if (!collateral) return false;

    const isSplMint = isCollateralSplMint(collateral);

    return isSplMint;
}

function useLoanInfoResponseMints(loans: LoanInfo[] | undefined) {
    const mints = useMemo(() => {
        if (!loans) return undefined;
        const set = new Set<string>();
        for (const loan of loans) {
            for (const ledger of loan.ledgers) {
                set.add(ledger.principalMint);
            }
            for (const collateral of loan.collateral) {
                set.add(collateral.assetMint);
            }
        }
        return Array.from(set);
    }, [loans]);
    const whirlpoolMints = useMemo(() => {
        if (!loans) return undefined;
        const set = new Set<string>();
        for (const loan of loans) {
            for (const c of loan.collateral) {
                if (c.assetType === AssetTypeIdentifier.OrcaPosition) set.add(c.assetMint);
            }
        }
        return Array.from(set);
    }, [loans]);
    const whirlpoolAddresses = useMemo(() => {
        if (!loans) return undefined;
        const set = new Set<string>();
        for (const loan of loans) {
            for (const c of loan.collateral) {
                if (c.assetType === AssetTypeIdentifier.OrcaPosition) set.add(c.assetIdentifier);
            }
        }
        return Array.from(set);
    }, [loans]);
    const raydiumMints = useMemo(() => {
        if (!loans) return undefined;
        const set = new Set<string>();
        for (const loan of loans) {
            for (const c of loan.collateral) {
                if (c.assetType === AssetTypeIdentifier.RaydiumPosition) set.add(c.assetMint);
            }
        }
        return Array.from(set);
    }, [loans]);
    const raydiumAddresses = useMemo(() => {
        if (!loans) return undefined;
        const set = new Set<string>();
        for (const loan of loans) {
            for (const c of loan.collateral) {
                if (c.assetType === AssetTypeIdentifier.RaydiumPosition) set.add(c.assetIdentifier);
            }
        }
        return Array.from(set);
    }, [loans]);

    return { mints, whirlpoolMints, whirlpoolAddresses, raydiumMints, raydiumAddresses };
}

export function calculateLeverageMultiplier(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;

    const ltv = calculateLoanHealth(loanExpanded).ltv;

    const leverageMultiplier = 1 / (1 - ltv);

    return leverageMultiplier;
}

interface UseLoanGraphDataParams {
    loanAddress: string;
    loanExpanded: AbfLoanExpanded | undefined;
    startTimestamp: number;
    intervalSeconds?: number;
    maxPoints?: number;
    projectionSeconds?: number;
    currentOraclePrice?: number;
}

/**
 * Hook to fetch all graph data (Position Value, PnL, Rate History) from the BE
 * via the integrated PnL endpoint and transform it into the format expected by the FE chart components.
 * Returns UI-formatted PnL stats (lamports -> UI amounts) similar to useTimelocks.
 */
type LoanPositionData = {
    positionValueData: PositionValueChartData[];
    pnlData: PnlChartData[];
    rateHistoryData: RateHistoryChartData[];
    eventsData: LoanChartsEvent[];
    livePnl: LoanPnlStats | null;
    currentOraclePrice?: number;
    isLoading: boolean;
    error: FetchBaseQueryError | SerializedError | undefined;
    isSuccess: boolean;
};

export function useLoanPositionData({
    loanAddress,
    loanExpanded,
    startTimestamp,
    intervalSeconds,
    maxPoints = 500,
    projectionSeconds,
    currentOraclePrice
}: UseLoanGraphDataParams): LoanPositionData {
    const {
        convertPositionValueChartData,
        convertPnlChartData,
        convertRateHistoryChartData,
        convertLoanChartsEvents,
        getMetadata
    } = useAbfTypesToUiConverter();

    // Calculate interval based on time range (same logic as FE)
    const calculatedInterval = useMemo(() => {
        if (intervalSeconds) return intervalSeconds;
        const totalDuration = getUnixTs() - startTimestamp;
        return totalDuration < TIME.DAY ? TIME.HOUR : TIME.DAY;
    }, [intervalSeconds, startTimestamp]);

    // Get collateral decimals for converting chart data
    const collateralDecimals = useMemo(() => {
        if (!loanExpanded) return undefined;
        return getMetadata(
            getZcLoanCollateral(loanExpanded)?.metadata?.mint ?? loanExpanded.pastCollateral[0]?.metadata?.mint
        )?.decimals;
    }, [loanExpanded, getMetadata]);

    // Live pnl is already on `loanExpanded.pnlStats` (bundled inline by /loans/:address?includePnl=true
    // or /loans/info with includePnl:true). No separate /loans/pnl fetch needed.
    const livePnlData = loanExpanded?.pnlStats ?? null;

    // Fetch PnL graph data using the dedicated graph endpoint /loans/pnl/history
    // Note: We omit endTimestamp to prevent unnecessary refetches as time passes.
    // The backend will default to current time when processing the request.
    const {
        data: chartDataResponse,
        isLoading: isChartDataLoading,
        error,
        isSuccess
    } = useLoanPnlGraphQuery(
        {
            loans: [loanAddress],
            startTimestamp,
            // Don't include endTimestamp - it would change every second and cause refetches
            intervalSeconds: calculatedInterval,
            maxPoints,
            projectionSeconds,
            includeGraphPnl: true,
            includeGraphPositionValue: true,
            includeGraphRateHistory: true
        },
        {
            skip: !loanAddress || !startTimestamp,
            // Disable polling and refetching - graph data should only be fetched once per parameter change
            refetchOnMountOrArgChange: false,
            refetchOnFocus: false,
            refetchOnReconnect: false
        }
    );

    const rawChartData = useMemo(() => {
        if (!chartDataResponse || !loanAddress) return undefined;
        return chartDataResponse[loanAddress];
    }, [chartDataResponse, loanAddress]);

    // Transform BE response to FE chart data format using converter functions
    const chartData = useMemo(() => {
        if (!rawChartData) {
            return {
                positionValueData: [],
                pnlData: [],
                rateHistoryData: [],
                eventsData: []
            };
        }

        // Use converter functions to transform backend data to frontend chart data
        const positionValueData = convertPositionValueChartData(rawChartData.positionValueData?.dataPoints ?? []);
        const pnlData = convertPnlChartData(rawChartData.pnlData?.dataPoints ?? [], collateralDecimals);
        // Remove projection data from rate history
        const rateHistoryData = convertRateHistoryChartData(
            rawChartData.rateHistoryData?.dataPoints.filter((d) => !d.isProjection) ?? []
        );

        const eventsData = convertLoanChartsEvents(rawChartData.markers ?? []);

        const { livePnlChartData, livePositionValueChartData, liveRateHistoryChartData } = getLiveLoanChartData(
            loanExpanded,
            livePnlData,
            currentOraclePrice
        );

        return {
            positionValueData: [...positionValueData, ...livePositionValueChartData],
            pnlData: [...pnlData, ...livePnlChartData],
            rateHistoryData: [...rateHistoryData, ...liveRateHistoryChartData],
            eventsData
        };
    }, [
        convertPositionValueChartData,
        convertPnlChartData,
        convertRateHistoryChartData,
        convertLoanChartsEvents,
        loanExpanded,
        rawChartData,
        livePnlData,
        currentOraclePrice,
        collateralDecimals
    ]);

    const isLoading = isChartDataLoading;

    return { ...chartData, livePnl: livePnlData, currentOraclePrice, isLoading, error, isSuccess };
}

function getLiveLoanChartData(
    loanExpanded: AbfLoanExpanded | undefined,
    livePnlData: LoanPnlStats | null,
    collateralPrice: number | undefined
) {
    if (!livePnlData || !loanExpanded)
        return {
            livePnlChartData: [],
            livePositionValueChartData: [],
            liveRateHistoryChartData: []
        };
    const {
        collateral: collateralArray,
        principalUsdPrice,
        principalMarketPrice,
        principalMetadata: { decimals: principalDecimals },
        netApyLive: netApy,
        collateralYieldPct: collateralApy
    } = loanExpanded;
    const { usdPnl, collateralPnl: tokenPnl } = livePnlData;

    const ledger = getZcLedger(loanExpanded);
    const collateral = getZcLoanCollateral(loanExpanded);

    const { assetMint: collateralMint, amount: collateralTokenAmount } = collateral ?? {};
    const { apy: borrowApy } = ledger ?? {};

    const now = getUnixTs();

    // Calculate current values
    const collateralValueUsd = collateralArray
        ? bsMath.add(...collateralArray.map((c) => bsMath.tokenAMul(c.amount, c.usdPrice, c.metadata?.decimals)))
        : undefined;
    const principalValueTokenAmount = summarizeLedgerAccount(ledger).totalOutstanding;
    const principalValueUsd = bsMath.tokenAMul(principalValueTokenAmount, principalUsdPrice, principalDecimals);
    const collateralTokenAmountOfDebt = bsMath.div(principalValueUsd, collateralPrice);
    const netPositionValueTokenAmount = bsMath.sub(collateralTokenAmount, collateralTokenAmountOfDebt);
    const netPositionValueUsd = bsMath.sub(collateralValueUsd, principalValueUsd);
    const netCollateralTransferUsdAmount = bsMath.sub(netPositionValueUsd, usdPnl);
    const netCollateralTransferTokenAmount = bsMath.sub(netPositionValueTokenAmount, tokenPnl);

    // The position-value chart values debt at the off-chain market price on the live point,
    // falling back to the on-chain price when no market price is available.
    const principalValueUsdMarket = bsMath.tokenAMul(
        principalValueTokenAmount,
        principalMarketPrice ?? principalUsdPrice,
        principalDecimals
    );
    const collateralTokenAmountOfDebtMarket = bsMath.div(principalValueUsdMarket, collateralPrice);
    const netPositionValueUsdMarket = bsMath.sub(collateralValueUsd, principalValueUsdMarket);

    const livePnlChartData: PnlChartData[] = !livePnlData
        ? []
        : [
              {
                  date: now,
                  principalValueUsd,
                  collateralValueUsd,
                  netPositionValueUsd,
                  netPositionValueTokenAmount,
                  usdPnl,
                  tokenPnl,
                  collateralPrice,
                  netCollateralTransferUsdAmount,
                  netCollateralTransferTokenAmount,
                  isProjection: false,
                  collateralMint
              }
          ];

    const livePositionValueChartData: PositionValueChartData[] =
        collateralTokenAmount === null
            ? []
            : [
                  {
                      date: now,
                      collateralTokenAmount,
                      principalValueUsd: principalValueUsdMarket,
                      collateralValueUsd,
                      netPositionValueUsd: netPositionValueUsdMarket,
                      netPositionValueTokenAmount,
                      collateralTokenAmountOfDebt: collateralTokenAmountOfDebtMarket,
                      isProjection: false,
                      collateralMint
                  }
              ];

    const liveRateHistoryChartData: RateHistoryChartData[] =
        borrowApy === undefined || netApy === undefined
            ? []
            : [
                  {
                      date: now,
                      borrowApy,
                      netApy,
                      collateralApy: collateralApy ?? 0,
                      collateralMint
                  }
              ];

    return {
        livePnlChartData,
        livePositionValueChartData,
        liveRateHistoryChartData
    };
}

function useUserLoansRawFilter(filter: Partial<UserLoansFilter>, defaultOrderFundingType: number) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const rawFilter = useMemo((): UserLoansFilter => {
        return {
            userAddress: activeWallet,
            filterType: filter.filterType ?? LoanFilterType.Active,
            orderFundingTypes: filter.orderFundingTypes ?? defaultOrderFundingType,
            principalMints: filter.principalMints ?? [],
            collateralMints: filter.collateralMints ?? [],
            collateralIdentifiers: filter.collateralIdentifiers ?? [],
            sortDirection: filter.sortDirection ?? SortDirection.Desc,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 25
        };
    }, [
        activeWallet,
        filter.filterType,
        filter.orderFundingTypes,
        filter.principalMints,
        filter.collateralMints,
        filter.collateralIdentifiers,
        filter.sortDirection,
        filter.page,
        filter.pageSize,
        defaultOrderFundingType
    ]);
    return { rawFilter, activeWallet };
}

export type UserLoopGroupsResult = {
    groups: UserLoopGroup[] | undefined;
    totalGroups: number | undefined;
    aggregate: LoansAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

/**
 * Wallet-scoped **loop** loan groups (`POST /user/loans` with
 * `orderFundingTypes = Loop`), keyed by collateral asset. Each group carries
 * loop-specific metrics (`wAvgHealth`, `wAvgNetApy`, `netPositionTokenAmount`,
 * `pnlPctChange`) plus per-loan `rowHealths` for the visible page.
 *
 * Same layering as {@link useUserStrategies} in {@link ./strategies}.
 */
export function useUserLoopGroups(filter: Partial<UserLoansFilter>, skip?: boolean): UserLoopGroupsResult {
    const { rawFilter, activeWallet } = useUserLoansRawFilter(filter, AbfOrderFundingType.Loop);

    const shouldSkip = !activeWallet || skip;
    const { data, isLoading: queryLoading, isFetching } = useUserLoansGroupsQuery(rawFilter, { skip: shouldSkip });

    const { tokensLoading, getMetadata } = useAbfTypesToUiConverter();
    const isLoading = queryLoading || tokensLoading;

    const groups = useMemo((): UserLoopGroup[] | undefined => {
        if (isLoading) return undefined;
        if (shouldSkip || !data) return [];
        return data.loopGroups
            .map((g: ApiUserLoopGroup): UserLoopGroup | undefined => {
                const collateralMetadata = getMetadata(g.collateralIdentifier) ?? getMetadata(g.collateralMint);
                if (!collateralMetadata) return undefined;
                return {
                    collateralMint: g.collateralMint,
                    collateralIdentifier: g.collateralIdentifier,
                    //this is the group's TOTAL collateral in native units
                    netPositionTokenAmount: Number(g.netPositionTokenAmount) || 0,
                    pnlPctChange: g.pnlPctChange,
                    aggregate: g.aggregate,
                    wAvgHealth: g.wAvgHealth,
                    wAvgNetApy: g.wAvgNetApy,
                    rowHealths: g.rowHealths,
                    collateralMetadata
                };
            })
            .filter(filterTruthy);
    }, [isLoading, shouldSkip, data, getMetadata]);

    return {
        groups: isLoading ? undefined : groups,
        totalGroups: data?.pageInfo.totalItems,
        aggregate: data?.aggregate,
        snapshotTs: data?.snapshotTs,
        isLoading,
        isFetching
    };
}

export type UserLoanGroupsResult = {
    groups: UserLoanGroup[] | undefined;
    totalGroups: number | undefined;
    aggregate: LoansAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

/**
 * Wallet-scoped **non-loop** loan groups (`POST /user/loans` with
 * `orderFundingTypes = Standard`), keyed by principal mint. The aggregate's
 * collateral-side fields are zero per backend contract — collateral isn't
 * naturally partitioned by principal mint for borrowers. Use `rowHealths`
 * for per-loan health on the visible page; the group has no `wAvgHealth` /
 * `wAvgNetApy` (those are loop-only).
 */
export function useUserLoanGroups(filter: Partial<UserLoansFilter>, skip?: boolean): UserLoanGroupsResult {
    const { rawFilter, activeWallet } = useUserLoansRawFilter(filter, AbfOrderFundingType.Standard);

    const shouldSkip = !activeWallet || skip;
    const { data, isLoading: queryLoading, isFetching } = useUserLoansGroupsQuery(rawFilter, { skip: shouldSkip });

    const { tokensLoading, getMetadata } = useAbfTypesToUiConverter();
    const isLoading = queryLoading || tokensLoading;

    const groups = useMemo((): UserLoanGroup[] | undefined => {
        if (isLoading) return undefined;
        if (shouldSkip || !data) return [];
        return data.loanGroups
            .map((g: ApiUserLoanGroup): UserLoanGroup | undefined => {
                const principalMetadata = getMetadata(g.principalMint);
                if (!principalMetadata) return undefined;
                return {
                    principalMint: g.principalMint,
                    borrowedTokenAmount: Number(g.borrowedTokenAmount) || 0,
                    aggregate: g.aggregate,
                    rowHealths: g.rowHealths,
                    principalMetadata
                };
            })
            .filter(filterTruthy);
    }, [isLoading, shouldSkip, data, getMetadata]);

    return {
        groups: isLoading ? undefined : groups,
        totalGroups: data?.pageInfo.totalItems,
        aggregate: data?.aggregate,
        snapshotTs: data?.snapshotTs,
        isLoading,
        isFetching
    };
}

export function useStandardLoansGroupedByCollateralKey(
    visibleGroupKeys: readonly string[],
    options?: { skip?: boolean }
): {
    allLoans: AbfLoanExpanded[] | undefined;
    loansByGroupKey: Map<string, AbfLoanExpanded[]> | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const collateralIdentifiers = useMemo(
        () => uniqueCollateralIdentifiersFromLoanGroupKeys(visibleGroupKeys),
        [visibleGroupKeys]
    );

    const loanFilter = useMemo(
        (): LoanFilterApi => ({
            loanAddresses: [],
            lenders: [],
            borrowers: activeWallet ? [activeWallet] : [],
            principalMints: [],
            sortSide: SortDirection.Desc,
            collateralMints: [],
            collateralIdentifiers,
            sortType: LoanInfoSorting.Principal,
            orderFundingTypes: AbfOrderFundingType.Standard
        }),
        [activeWallet, collateralIdentifiers]
    );

    const listPagination = useMemo(
        (): LoanPaginationInfo => ({
            loanStatusType: LoanStatusType.ActiveOnly,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE,
            sortSide: SortDirection.Desc,
            sortType: LoanInfoSorting.Principal,
            principalMint: undefined,
            collateralMint: undefined
        }),
        []
    );

    const skip =
        options?.skip ||
        !activeWallet ||
        visibleGroupKeys.length === 0 ||
        (visibleGroupKeys.length > 0 && collateralIdentifiers.length === 0);

    const { data, isLoading, isFetching } = useLoanInfos({
        loanFilter,
        pagination: listPagination,
        skip
    });

    const filteredLoans = useMemo(() => {
        if (!data || visibleGroupKeys.length === 0) return undefined;
        const allowed = new Set(visibleGroupKeys);
        return data.filter((loan) => {
            const k = loanCollateralGroupTableKey(loan);
            return k != null && allowed.has(k);
        });
    }, [data, visibleGroupKeys]);

    const loansByGroupKey = useMemoizedGroupedMap(filteredLoans, (loan) => loanCollateralGroupTableKey(loan));

    return {
        allLoans: filteredLoans,
        loansByGroupKey,
        isLoading: isLoading || isFetching
    };
}

/**
 * Per-mint loan-info fetcher for non-loop loan groups. Mirror of
 * {@link useStandardLoansGroupedByCollateralKey} but keyed by principal
 * mint — used by `LoansSection` (and other principal-mint-grouped views).
 */
export function useStandardLoansGroupedByPrincipalMint(
    visibleMints: readonly string[],
    options?: { skip?: boolean }
): {
    allLoans: AbfLoanExpanded[] | undefined;
    loansByMint: Map<string, AbfLoanExpanded[]> | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const principalMints = useMemo(() => Array.from(new Set(visibleMints)), [visibleMints]);

    const loanFilter = useMemo(
        (): LoanFilterApi => ({
            loanAddresses: [],
            lenders: [],
            borrowers: activeWallet ? [activeWallet] : [],
            principalMints,
            sortSide: SortDirection.Desc,
            collateralMints: [],
            collateralIdentifiers: [],
            sortType: LoanInfoSorting.Principal,
            orderFundingTypes: AbfOrderFundingType.Standard
        }),
        [activeWallet, principalMints]
    );

    const listPagination = useMemo(
        (): LoanPaginationInfo => ({
            loanStatusType: LoanStatusType.ActiveOnly,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE,
            sortSide: SortDirection.Desc,
            sortType: LoanInfoSorting.Principal,
            principalMint: undefined,
            collateralMint: undefined
        }),
        []
    );

    const skip = options?.skip || !activeWallet || principalMints.length === 0;

    const { data, isLoading, isFetching } = useLoanInfos({
        loanFilter,
        pagination: listPagination,
        skip,
        // The borrow table renders Debt/Collateral/Health/APY — never PnL. Skipping the per-loan
        // PnL pipeline drops this call (the page's slowest) from ~6.5s to the summary-only path.
        includePnl: false
    });

    const filteredLoans = useMemo(() => {
        if (!data || principalMints.length === 0) return undefined;
        const allowed = new Set(principalMints);
        return data.filter((loan) => {
            const mint = loanPrincipalGroupTableKey(loan);
            return mint != null && allowed.has(mint);
        });
    }, [data, principalMints]);

    const loansByMint = useMemoizedGroupedMap(filteredLoans, (loan) => loanPrincipalGroupTableKey(loan));

    return {
        allLoans: filteredLoans,
        loansByMint,
        isLoading: isLoading || isFetching
    };
}

export function useUserStandardLoansPaginated({
    pagination,
    principalGroupKey,
    skip
}: {
    pagination: LoanPaginationInfo;
    principalGroupKey: AllValueType | string | number;
    skip?: boolean;
}): {
    positions: AbfLoanExpanded[] | undefined;
    totalCount: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const principalMint = isAll(principalGroupKey) ? undefined : String(principalGroupKey);

    const loanFilter = useMemo(
        (): LoanFilterApi => ({
            loanAddresses: [],
            lenders: [],
            borrowers: activeWallet ? [activeWallet] : [],
            principalMints: principalMint ? [principalMint] : [],
            sortSide: SortDirection.Desc,
            collateralMints: [],
            collateralIdentifiers: [],
            sortType: LoanInfoSorting.Principal,
            orderFundingTypes: AbfOrderFundingType.Standard
        }),
        [activeWallet, principalMint]
    );

    // useLoanInfos scopes principalMints from pagination.principalMint, so thread the group key there.
    const scopedPagination = useMemo(
        (): LoanPaginationInfo => ({ ...pagination, principalMint }),
        [pagination, principalMint]
    );

    const { data, totalLoans, isLoading, isFetching } = useLoanInfos({
        loanFilter,
        pagination: scopedPagination,
        skip: skip || !activeWallet,
        includePnl: false
    });

    return { positions: data, totalCount: totalLoans, isLoading, isFetching };
}

export function useMergeLoanPlanQuote(loanAddresses: string[] | undefined) {
    const { convertMergeLoanPlan } = useAbfTypesToUiConverter();
    const addresses = useMemo(() => loanAddresses ?? [], [loanAddresses]);
    const canQuery = addresses.length > 0;

    const request = useMemo((): MergePlanQuoteRequest => ({ loans: addresses }), [addresses]);

    const query = useMergePlanQuoteQuery(canQuery ? request : skipToken, {
        skip: !canQuery
    });

    const data = useMemo(
        () => (query.data ? convertMergeLoanPlan(query.data) : undefined),
        [query.data, convertMergeLoanPlan]
    );

    return { ...query, data };
}
